import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function ForcePasswordChange() {
  const { changePassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords do not match"); return; }
    if (next.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-[#F8FAFC]" data-testid="force-password-change">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl shadow-sm p-8">
        <div className="h-12 w-12 rounded-full bg-[#2EA3FD]/10 grid place-items-center mb-5">
          <ShieldCheck className="text-[#2EA3FD]" size={24} />
        </div>
        <h1 className="text-2xl font-extrabold text-[#0B1A2F] font-display">Set a new password</h1>
        <p className="text-sm text-slate-500 mt-1 mb-6">You're using a temporary password. Please choose a new one to continue.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="current">Temporary password</Label>
            <Input id="current" data-testid="fpc-current-input" type="password" value={current} required
              onChange={(e) => setCurrent(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="new">New password</Label>
            <Input id="new" data-testid="fpc-new-input" type="password" value={next} required
              onChange={(e) => setNext(e.target.value)} className="mt-1" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input id="confirm" data-testid="fpc-confirm-input" type="password" value={confirm} required
              onChange={(e) => setConfirm(e.target.value)} className="mt-1" />
          </div>
          <Button type="submit" data-testid="fpc-submit-btn" disabled={loading}
            className="w-full bg-[#2EA3FD] hover:bg-[#1B8EE6] text-white font-semibold">
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>
        <div className="text-center mt-4">
          <button onClick={logout} data-testid="fpc-signout-btn" className="text-sm text-slate-500 hover:underline">Sign out</button>
        </div>
      </div>
    </div>
  );
}
